export const AI_CHAT_LAUNCHER_MARGIN = 50
export const AI_CHAT_POPPER_GAP = 12
export const AI_CHAT_LAUNCHER_FALLBACK_SIZE = 56
export const AI_CHAT_PERSISTED_TRIGGER_MARGIN = 16
export const AI_CHAT_BUTTON_POSITION_STORAGE_KEY = 'ai-chat-button-position'
export const AI_CHAT_PILL_POSITION_STORAGE_KEY = 'ai-chat-pill-position'
export const AI_CHAT_MOBILE_BREAKPOINT = 768
// Historical name: this breakpoint now controls docking availability only.
export const AI_CHAT_FIXED_PANEL_BREAKPOINT = 1024

export interface AiChatTriggerPosition {
  x: number
  y: number
  /** Normalized top-left position used to keep dragged launchers stable across browser zoom levels. */
  viewportXRatio?: number
  viewportYRatio?: number
}
export interface AiChatTriggerCoordinateOptions {
  placement: 'bottom-right' | 'bottom-center'
  visualMargin: number
  persistedMargin: number
}
export interface AiChatTriggerViewport { width: number, height: number }
export interface AiChatTriggerBounds { left: number, top: number, width: number, height: number }

// The persisted margin is a minimum distance to the bottom-right corner, so both
// converters cap the coordinates instead of allowing flush placements (which also
// sanitizes legacy stored values that were saved flush into the corner).
export const toRenderedTriggerPosition = (
  position: AiChatTriggerPosition,
  options: AiChatTriggerCoordinateOptions,
): AiChatTriggerPosition => {
  const marginDelta = options.visualMargin - options.persistedMargin
  const rightAnchored = options.placement === 'bottom-right'
  return {
    x: rightAnchored ? Math.min(position.x + marginDelta, marginDelta) : position.x,
    y: Math.min(position.y + marginDelta, marginDelta),
  }
}

export const toPersistedTriggerPosition = (
  position: AiChatTriggerPosition,
  options: AiChatTriggerCoordinateOptions,
): AiChatTriggerPosition => {
  const marginDelta = options.visualMargin - options.persistedMargin
  const rightAnchored = options.placement === 'bottom-right'
  return {
    x: rightAnchored ? Math.min(position.x - marginDelta, 0) : position.x,
    y: Math.min(position.y - marginDelta, 0),
  }
}

const clampRatio = (value: number) => Math.max(0, Math.min(1, value))

export const withViewportRelativeTriggerPosition = (
  position: AiChatTriggerPosition,
  bounds: AiChatTriggerBounds,
  viewport: AiChatTriggerViewport,
): AiChatTriggerPosition => {
  const availableWidth = Math.max(0, viewport.width - bounds.width)
  const availableHeight = Math.max(0, viewport.height - bounds.height)
  return {
    ...position,
    viewportXRatio: availableWidth > 0 ? clampRatio(bounds.left / availableWidth) : 0,
    viewportYRatio: availableHeight > 0 ? clampRatio(bounds.top / availableHeight) : 0,
  }
}

export const toViewportStableRenderedTriggerPosition = (
  position: AiChatTriggerPosition,
  options: AiChatTriggerCoordinateOptions,
  viewport: AiChatTriggerViewport,
  triggerSize: { width: number, height: number },
): AiChatTriggerPosition => {
  if (!Number.isFinite(position.viewportXRatio) || !Number.isFinite(position.viewportYRatio)) {
    return toRenderedTriggerPosition(position, options)
  }

  const availableWidth = Math.max(0, viewport.width - triggerSize.width)
  const availableHeight = Math.max(0, viewport.height - triggerSize.height)
  const left = clampRatio(Number(position.viewportXRatio)) * availableWidth
  const top = clampRatio(Number(position.viewportYRatio)) * availableHeight
  const baseLeft = options.placement === 'bottom-right'
    ? viewport.width - triggerSize.width - options.visualMargin
    : availableWidth / 2
  const baseTop = viewport.height - triggerSize.height - options.visualMargin

  return { x: left - baseLeft, y: top - baseTop }
}

export const isAiChatMobileViewport = (width: number) => width < AI_CHAT_MOBILE_BREAKPOINT
export const isAiChatFixedPanelViewport = (width: number) => width <= AI_CHAT_FIXED_PANEL_BREAKPOINT
